import { Location } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  Query,
} from '@angular/fire/compat/firestore';
import { Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  debounceTime,
  from,
  mergeMap,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import { Assignment } from '../../models/assignment.school.model';
import { UserModel } from '../../models/user.model';
import { SchoolAssignmentService } from '../../services/assignment.school.service';
import * as assignmentActions from '../actions/assignment.school.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';

@Injectable()
export class SchoolAssignmentEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  assignmentService: SchoolAssignmentService = inject(SchoolAssignmentService);
  toastr: ToastrService = inject(ToastrService);
  afs: AngularFirestore = inject(AngularFirestore);
  logger: NGXLogger = inject(NGXLogger);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  location: Location = inject(Location);

  user: UserModel | null = null;

  private logout$ = new Subject<void>();

  constructor() {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.logout$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  // Effect to fetch Pending Assignments
  getAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignmentActions.getAssignments),
      debounceTime(500),
      mergeMap(
        ({
          groupId,
          gradeId,
          academicYear,
          createdById,
          fromDate,
          toDate,
          lastRecord,
        }) => {
          this.logger.log(
            `getAssignments$: groupId: ${groupId}, gradeId: ${gradeId}, createdById: ${createdById}, academicYear: ${academicYear}, fromDate: ${fromDate}, toDate: ${toDate}, lastRecord: ${lastRecord}`,
          );
          this.logout$.next();
          let query: AngularFirestoreCollection<Assignment> = this.afs
            .collection('groups')
            .doc(groupId)
            .collection('assignments', (ref) => {
              let reference = ref as Query;

              reference = ref.where('academicYear', '==', academicYear);

              if (gradeId) {
                reference = reference.where('gradeId', '==', gradeId);
              }
              if (createdById) {
                reference = reference.where('createdById', '==', createdById);
              }
              reference = reference.orderBy('modified', 'desc');

              if (lastRecord) reference = reference.startAfter(lastRecord);

              return reference;
            });
          return query.get();
        },
      ),
      mergeMap((assignments) => {
        // this.logger.log(`getAssignments$: Assignments: ${assignments.docs.length}`);
        const result: (Assignment | null)[] = assignments.docs.map((doc) => {
          try {
            const assignment = doc.data() as Assignment;
            assignment.id = doc.id;
            // this.logger.log(`Assignment: ${JSON.stringify(assignment)}`);
            return assignment;
          } catch (error) {
            this.logger.error(`Error transforming assignments: ${error}`);
            return null;
          }
        });
        const filtered = result.filter((value) => value !== null);
        if (filtered == null) return EMPTY;
        return of(
          assignmentActions.addAssignmentsToStore({ assignments: filtered }),
        );
      }),
      catchError((error) => {
        this.logger.error(`Error fetching assignments: ${error}`);
        this.toastr.error(
          'An error occurred. Please try again later.',
          'Error Fetching Assignments',
        );
        return of(assignmentActions.getAssignmentsFailure({ error }));
      }),
    ),
  );

  getAssignment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignmentActions.getAssignment),
      debounceTime(500),
      switchMap(({ groupId, assignmentId }) => {
        this.logger.log(
          `getAssignment$: groupId: ${groupId}, assignmentId: ${assignmentId}`,
        );
        return this.afs
          .collection('groups')
          .doc(groupId)
          .collection('assignments')
          .doc(assignmentId)
          .get();
      }),
      switchMap((doc) => {
        if (!doc.exists) {
          this.logger.log('getAssignment$: Assignment not found');
          return of(
            assignmentActions.getAssignmentFailure({
              error: {
                title: 'Assignment not found',
                message:
                  'The assignment could not be fetched. Please try again later.',
              },
            }),
          );
        }
        try {
          const assignment = doc.data() as Assignment;
          assignment.id = doc.id;
          this.logger.log(
            `getAssignment$: Assignment: ${JSON.stringify(assignment)}`,
          );
          return of(
            assignmentActions.addAssignmentToStore({ assignment: assignment }),
          );
        } catch (error) {
          return of(
            assignmentActions.getAssignmentFailure({
              error: {
                title: 'Assignment not found',
                message:
                  'The assignment could not be fetched. Please try again later.',
              },
            }),
          );
        }
      }),
      catchError((error) => {
        this.logger.error(`Error fetching assignment: ${error}`);
        this.toastr.error(
          'An error occurred. Please try again later.',
          'Error Fetching Assignment',
        );
        return of(assignmentActions.getAssignmentsFailure({ error }));
      }),
    ),
  );

  // Effect to create a new assignment
  create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(assignmentActions.create),
      debounceTime(500),
      mergeMap(({ groupId, assignmentId, assignment }) => {
        const create = {
          ...assignment,
          created: Timestamp.now(),
          modified: Timestamp.now(),
        };

        return from(
          this.assignmentService.create(groupId, assignmentId, create),
        ).pipe(
          mergeMap(() => {
            const localAssignment: Assignment = {
              ...assignment,
              id: assignmentId,
              created: Timestamp.fromDate(moment(assignment.created).toDate()),
              modified: Timestamp.fromDate(
                moment(assignment.modified).toDate(),
              ),
            };

            // Success notification and navigation
            this.toastr.success(
              'The assignment has been created successfully.',
              'Assignment Created',
            );
            // this.router.navigate(['school', groupId, 'assignments', assignment.id]);
            return of(
              assignmentActions.createSuccess({ assignment: localAssignment }),
            );
          }),
          catchError((error) => {
            this.logger.log(
              `Error creating assignment: ${JSON.stringify(error)}`,
            );
            this.toastr.error(
              `An error occurred during Assignment Creation. Please try again later.`,
              'Error Creating Assignment',
            );
            return of(assignmentActions.createFailure({ error }));
          }),
        );
      }),
    ),
  );

  // Effect to update a assignment
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(assignmentActions.update),
      mergeMap(({ groupId, assignmentId, assignment }) => {
        const updateAssignment = {
          ...assignment,
          modified: serverTimestamp() as Timestamp,
        };
        return from(
          this.assignmentService.update(
            groupId,
            assignmentId,
            updateAssignment,
          ),
        ).pipe(
          mergeMap(() => {
            const localAssignment: Assignment = {
              ...assignment,
              id: assignmentId,
              modified: Timestamp.fromDate(
                moment(assignment.modified).toDate(),
              ),
            };
            this.toastr.success(
              'The assignment details have been updated successfully.',
              'Assignment Updated',
            );
            return of(
              assignmentActions.updateSuccess({ assignment: localAssignment }),
            );
          }),
          catchError((error) => {
            this.logger.error(`Error updating assignment: ${error}`);
            this.toastr.error(
              `An error occurred while updating the assignment details. Please try again later.`,
              'Error Updating Assignment',
            );
            return of(assignmentActions.updateFailure({ error }));
          }),
        );
      }),
    ),
  );

  //   getAssignmentStats$: Observable<Action> = createEffect(() =>
  //     this.actions$.pipe(
  //       ofType(groupActions.select, assignmentActions.loadAssignmentStats),
  //       withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
  //       switchMap(([{ groupId }, group]) => {
  //         if (group == undefined) return EMPTY;
  //         this.logout$.next();
  //         return this.afs
  //           .collection('groups')
  //           .doc(group.id)
  //           .collection<any>('assignment_stats')
  //           .doc(`${group.id}_${group.academicYear}`)
  //           .get()
  //           .pipe(takeUntil(this.logout$))
  //           .pipe(
  //             switchMap((snapshot) => {
  //               if (!snapshot.exists) return EMPTY;
  //               // this.logger.log(`getGroupStatistics$ :: Stats found.`);
  //               const record: any = snapshot.data();
  //               let g: Group = {
  //                 id: group.id,
  //                 assignmentStats: record,
  //                 assignmentStatsLoaded: true,
  //               } as Group;

  //               // this.logger.log(
  //               //   `getAssignmentStats$ :: Stats to store: ${JSON.stringify(g)}`
  //               // );
  //               return of(groupActions.getAssignmentStatsDone({ group: g }));
  //             }),
  //             catchError((error) => {
  //               this.logger.log(
  //                 `getAssignmentStats$ :: Error: ${JSON.stringify(error)}`
  //               );
  //               return of(groupActions.getAssignmentStatsDone({ group: null }));
  //             })
  //           );
  //       })
  //     )
  //   );

  // Effect to update a assignment notes
  // updateNotes$: Observable<Action> = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(assignmentActions.updateNotes),
  //     switchMap(({ groupId, assignmentId, notesData }) => {

  //       return from(this.assignmentService.getUploadUrl(groupId, assignmentId)).pipe(take(1)).subscribe((response) => {

  //       return from(
  //         this.assignmentService.uploadNotes(response.url, notesData)
  //       ).pipe(
  //         switchMap(() => {
  //           const localAssignment: Assignment = {
  //             notesUrl: response.url,
  //             modified: Timestamp.fromDate(moment(assignment.modified).toDate()),
  //           };
  //           return of(
  //             assignmentActions.updateStatusSuccess({ assignment: localAssignment })
  //           );
  //         }),
  //         catchError((error) => {
  //           // this.logger.error(`Error updating assignment: ${error}`);
  //           this.toastr.error(
  //             `An error occurred, '${error}'. Please try again later.`,
  //             'Error Updating Assignment Status'
  //           );
  //           return of(assignmentActions.updateStatusFailure({ error }));
  //         })
  //       );
  //     })
  //   )
  // );

  // effect to delete a assignment
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignmentActions.deleteAction),
      switchMap(({ groupId, assignmentId: id }) =>
        this.assignmentService
          .delete(groupId, id)
          .then(() => {
            this.toastr.success(
              'The assignment has been deleted successfully.',
              'Assignment Deleted',
            );
            return assignmentActions.deleteSuccess({ id: id });
          })
          .catch((error: any) => {
            // this.logger.error(`Error deleting assignment: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Deleting Assignment',
            );
            return assignmentActions.deleteFailure({ error });
          }),
      ),
    ),
  );

  clearAssignments$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(assignmentActions.clear());
      }),
    ),
  );
}
